import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages, insideResources } from '../data/content';
import { OFFER_ANCHOR } from '../data/config';
import { CtaButton } from './CtaButton';
const AUTOPLAY_MS = 3500;
export function InsideLookSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragged = useRef(false);
  const reduce = useReducedMotion();
  const total = insidePages.length;
  const go = (dir: 1 | -1) => setIndex(i => (i + dir + total) % total);
  const page = insidePages[index];
  useEffect(() => {
    if (paused || reduce) return;
    const t = setTimeout(() => setIndex(i => (i + 1) % total), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, paused, reduce, total]);
  return <section className="bg-pale px-4 py-6 text-center md:px-12 md:py-8" data-id="element-104" data-mp="12se7rvpn59r0">
      <h2 className="font-heading text-[24px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-navy md:text-[32px]" data-id="element-105" data-mp="2o8gc6jvomk76">
        Take a look inside
      </h2>
      <p className="mt-1.5 font-heading text-[14px] font-bold uppercase leading-tight text-brand md:text-[16px]" data-id="element-106" data-mp="2sbuszcrg34lo">
        Explore Complete AAC Communication Toolkit™
      </p>
      <p className="mx-auto mt-1.5 max-w-[600px] text-[13px] leading-[1.45] text-ink md:text-[14px]" data-id="element-107" data-mp="327fu78oow0ud">
        Real pages from the collection — functional stimuli, language activities, autonomy scenarios, access materials,
        and case-based pathways, organized by intervention need.
      </p>

      <div className="relative mx-auto mt-4 max-w-[440px]" role="region" aria-roledescription="carousel" aria-label="Page previews" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} data-id="element-108" data-mp="2egsjygrawz4n">
        <div className="overflow-hidden rounded-md border border-line bg-white shadow-soft" data-id="element-109" data-mp="1iruwjghgndji">
          <AnimatePresence mode="wait" initial={false} data-id="element-110" data-mp="1eqhoczqway9f">
            <motion.a key={index} href={OFFER_ANCHOR} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragStart={() => dragged.current = true} onDragEnd={(_, info) => {
            if (info.offset.x < -50) go(1);else if (info.offset.x > 50) go(-1);
            setTimeout(() => dragged.current = false, 0);
          }} onClick={e => dragged.current && e.preventDefault()} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.18,
            ease: 'easeOut'
          }} className="block aspect-[4/5] w-full cursor-grab touch-pan-y active:cursor-grabbing" aria-label={`Preview ${index + 1} of ${total} — go to the special offer`} data-id="element-111" data-mp="1epzwefb9n67b">
              <img src={page.image} alt={page.alt} className="h-full w-full bg-white object-contain" draggable={false} data-id="element-112" data-mp="21u46oz7bnkq" />
            </motion.a>
          </AnimatePresence>
        </div>
        <button type="button" onClick={() => go(-1)} aria-label="Previous preview" className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white text-navy shadow-soft transition-colors duration-150 hover:text-brand" data-id="element-113" data-mp="3p608vemdpdlq">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-114" data-mp="bxx6buu1zm7o" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next preview" className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-white text-navy shadow-soft transition-colors duration-150 hover:text-brand" data-id="element-115" data-mp="bxnd7ydpoyxr">
          <ChevronRightIcon className="h-5 w-5" data-id="element-116" data-mp="3gwmbaza2pxyg" />
        </button>
      </div>

      <div className="mt-3 flex justify-center gap-1.5" role="tablist" aria-label="Choose preview" data-id="element-117" data-mp="3gurrjoajgbvy">
        {insidePages.map((p, i) => <button key={p.image} type="button" role="tab" onClick={() => setIndex(i)} aria-label={`Show preview ${i + 1}`} aria-selected={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-5 bg-navy' : 'w-2 bg-line'}`} data-id="element-118" data-mp="37vedeibgyz4w" data-mpi={i} />)}
      </div>

      <h3 className="mt-5 font-heading text-[15px] font-extrabold uppercase leading-tight text-navy md:text-[17px]" data-id="element-119" data-mp="3qth7hbivjm9k">
        Inside the collection, you'll find
      </h3>
      <dl className="mt-2.5 grid gap-x-6 text-left sm:grid-cols-2" data-id="element-120" data-mp="n673rrfep1w9">
        {insideResources.map(r => <div key={r.title} className="border-t border-line py-2.5" data-id="element-121" data-mp="ie79ofm0p37l">
            <dt className="font-heading text-[13px] font-extrabold uppercase leading-tight text-brand md:text-[14px]" data-id="element-122" data-mp="pqn01tcjfblo">
              {r.title}
            </dt>
            <dd className="mt-0.5 text-[13px] leading-[1.4] text-ink md:text-[14px]" data-id="element-123" data-mp="upq3588yj7r0">{r.text}</dd>
          </div>)}
      </dl>

      <p className="mt-4 font-heading text-[13px] font-extrabold uppercase leading-tight text-goldDark md:text-[15px]" data-id="element-124" data-mp="13pwh0v8ki9qp">
        Practical materials. Organized interventions. Professional clinical application.
      </p>

      <p className="mt-4 font-heading text-[14px] font-extrabold uppercase leading-tight text-navy md:text-[16px]" data-id="element-125" data-mp="1dnk9tfmm5768">
        Imagine opening your next session with the materials already prepared.
      </p>
      <div className="mt-2.5" data-id="element-126" data-mp="1nd3v0rxstd49">
        <CtaButton mode="scroll" subtext="See the special launch offer" data-id="element-127" data-mp="1ndc8akxu2kfz">
          I want all 11 AAC resources now!
        </CtaButton>
      </div>
    </section>;
}